import type { BridgeGroupProps, SingleMouthItem } from '@orthly/dentin';
import type { PatientDetailTreatmentCardScan_FragmentFragment } from '@orthly/graphql-inline-react';
import type { IOrderItemV2DTO } from '@orthly/items';
import { LabOrderItemSKUType, OrderItemV2Utils } from '@orthly/items';
import { getOrderItemsFromFragment } from '@orthly/veneer';
import _ from 'lodash';

interface PatientDetailHighlightedItems {
    bridges: BridgeGroupProps[];
    items: SingleMouthItem[];
}

export class PatientDetailTreatmentCardUtils {
    static getPatientDetailHighlightedItems(
        scan?: PatientDetailTreatmentCardScan_FragmentFragment | null,
    ): PatientDetailHighlightedItems {
        if (!scan) {
            return { items: [], bridges: [] };
        }

        const items = getOrderItemsFromFragment(scan.items);

        return {
            items: items.flatMap(item => this.getSingleToothItems(item)),
            bridges: _.compact(items.flatMap(item => this.getBridgeItems(item))),
        };
    }

    private static getSingleToothItems(item: IOrderItemV2DTO): SingleMouthItem[] {
        if (OrderItemV2Utils.itemIsType(item, [LabOrderItemSKUType.Bridge, LabOrderItemSKUType.ImplantBridge])) {
            return [];
        }

        const teeth = OrderItemV2Utils.getSingleToothUnits(item);

        return teeth.map(tooth => ({
            number: tooth.unn,
            highlighted: true,
            isImplant: OrderItemV2Utils.itemIsType(item, LabOrderItemSKUType.Implant),
        }));
    }

    private static getBridgeItems(item: IOrderItemV2DTO): BridgeGroupProps | undefined {
        if (!OrderItemV2Utils.itemIsType(item, [LabOrderItemSKUType.Bridge, LabOrderItemSKUType.ImplantBridge])) {
            return undefined;
        }

        const singleUnits = OrderItemV2Utils.getSingleToothUnits(item);

        return {
            highlighted: true,
            items: singleUnits
                .filter(unit => ['Crown', 'CrownPontic'].includes(unit.unit_type))
                .map(unit => ({
                    number: unit.unn,
                    pontic: unit.unit_type === 'CrownPontic',
                })),
        };
    }
}
